/**
 * Dynamic Widget Section Component
 *
 * Renders custom widget components based on section configuration.
 * Looks up widgets from the widget registry and passes props dynamically.
 */

import React from 'react';
import { TabSection, WidgetSectionConfig } from '../../../types/tabConfiguration';
import { getWidget, widgetExists, getAllWidgetNames } from '../widgets/widgetRegistry';
import { AlertCircle, Layout } from 'lucide-react';

interface DynamicWidgetSectionProps {
  /** Section configuration */
  section: TabSection;
  /** Optional record ID for data fetching */
  recordId?: string;
  /** Optional custom class name */
  className?: string;
}

/**
 * Dynamic Widget Section
 *
 * Renders custom widgets from the widget registry
 */
const DynamicWidgetSection: React.FC<DynamicWidgetSectionProps> = ({
  section,
  recordId,
  className = ''
}) => {
  // Cast config to WidgetSectionConfig
  const config = section.config as WidgetSectionConfig;

  // Get the widget component name from config
  const componentName = config?.componentName;

  // Check if component name is provided
  if (!componentName) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-start">
          <AlertCircle className="h-8 w-8 text-red-500 mr-4 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {section.label}
            </h3>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                Configuration Error
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">
                Widget component name is missing in section configuration.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if widget exists in registry
  if (!widgetExists(componentName)) {
    const availableWidgets = getAllWidgetNames();

    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-start">
          <AlertCircle className="h-8 w-8 text-yellow-500 mr-4 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {section.label}
            </h3>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-2">
                Widget Not Found
              </p>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-3">
                Widget component "{componentName}" is not registered in the widget registry.
              </p>
              <div className="mt-3">
                <p className="text-xs text-yellow-700 dark:text-yellow-300 font-medium mb-1">
                  Available widgets:
                </p>
                <ul className="text-xs text-yellow-600 dark:text-yellow-400 list-disc list-inside">
                  {availableWidgets.length > 0 ? (
                    availableWidgets.map(name => (
                      <li key={name}>{name}</li>
                    ))
                  ) : (
                    <li>No widgets registered</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get the widget component
  const WidgetComponent = getWidget(componentName);

  if (!WidgetComponent) {
    // This should never happen if widgetExists returns true, but handle it just in case
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-start">
          <AlertCircle className="h-8 w-8 text-red-500 mr-4 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {section.label}
            </h3>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                Widget Loading Error
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to load widget component "{componentName}".
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Prepare props to pass to the widget
  const widgetProps = {
    recordId,
    ...(config.props || {}),
  };

  /**
   * Render Widget with Props
   */
  return (
    <div className={`${className}`}>
      {/* Optional Section Header */}
      {(section.label || section.description) && (
        <div className="mb-4">
          <div className="flex items-start">
            <Layout className="h-6 w-6 text-purple-500 mr-3 mt-1" />
            <div>
              {section.label && (
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {section.label}
                </h3>
              )}
              {section.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {section.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Render the Widget Component */}
      <WidgetComponent {...widgetProps} />
    </div>
  );
};

export default DynamicWidgetSection;
